// AUTO-GENERATED — do not edit. Source: /shared/. Run scripts/sync-shared.sh to update.
/**
 * SLIK — Client Identity (BRAIN, 2026-08-07)
 * ==========================================
 * ONE way to key a person across the POS, customer-portal and public-booking. Two records are the
 * SAME person if their PHONE matches (primary), else email, else name — all normalized. Phones are
 * stored in many formats ("(856) 979-9044", "856-979-9044", "8569799044"); every lookup MUST
 * normalize to digits on BOTH sides or the match silently fails. That exact miss let a blocked
 * client (dumb dumb) rebook online: a Firestore where('phone','==',digits) never matched the
 * formatted stored value, so the no-show block was skipped.
 *
 * Pure logic — NO React, NO Firebase. Edit THIS file at repo root and run scripts/sync-shared.sh.
 * Do NOT edit the per-app copies in <app>/src/shared/.
 */

/** Phone → canonical identity key (last 10 digits; US country code collapsed). */
export function normalizePhone(p?: string | null): string {
  const d = (p || '').replace(/\D/g, '');
  // Collapse the US country code so "+1 (973) 600-2384", "1 973…" and "973…" are ONE identity.
  // Online/Fresha bookings capture +1 (11 digits) while stored records are 10-digit → digits-only
  // never matched, so a RETURNING client was treated as NEW → 45-min window → 15-min overlap with the
  // next appt. Last-10 is the same canonical key the app's phoneKey/messaging paths use. (Jose 2026-10-21)
  return d.length > 10 ? d.slice(-10) : d;
}

/** A usable phone has at least 7 digits (rejects blanks / partials). */
export function isUsablePhone(p?: string | null): boolean {
  return normalizePhone(p).length >= 7;
}

/** Lowercased/trimmed text (email or name). */
export function normalizeText(s?: string | null): string {
  return (s || '').trim().toLowerCase();
}

export interface PersonLite {
  phone?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
}

/** Full name for a record (tolerates firstName/lastName or a single `name`). */
function fullName(p: PersonLite): string {
  const joined = [p.firstName, p.lastName].filter(Boolean).join(' ');
  return normalizeText(joined || p.name);
}

/**
 * Does candidate `c` refer to the same person as `who`? Phone match wins; email and name are
 * fallbacks for when no phone was captured. A blank/short phone never matches on phone.
 */
export function isSamePerson(who: PersonLite, c: PersonLite): boolean {
  const wp = normalizePhone(who.phone);
  if (wp.length >= 7 && normalizePhone(c.phone) === wp) return true;
  const we = normalizeText(who.email);
  if (we && normalizeText(c.email) === we) return true;
  const wn = fullName(who);
  if (wn && fullName(c) === wn) return true;
  return false;
}

/** First candidate in `list` that is the same person as `who` (phone-first). */
export function findSamePerson<T extends PersonLite>(who: PersonLite, list: T[]): T | undefined {
  return list.find((c) => isSamePerson(who, c));
}
